/**
 * WorkItemCombobox
 * ------------------------------------------------------------------
 * 「行の中で打って探して Enter で確定する」ための汎用コンボボックス。
 *
 * 設計: docs/design/work-item-picker-ux.md §6.2 / §6.5
 *  - 請求書（業務マスタ）と計算書（料金表）の両方で使う共通部品。
 *    特定の API に依存させないため、候補は props で受け取る。
 *  - 候補には**必ず単価を出す**（D5 の是正）。
 *  - 件数で無言に切り捨てない。上限を超えた分は「他 N 件」と明示する（D4 の是正）。
 *  - キーボードだけで完結する（↑↓ / Enter / Esc / Tab）。
 */
import {
  forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState,
} from "react";
import type { CSSProperties, ReactNode } from "react";
import { formatJPY } from "@juro/shared";

export type ComboboxOption = {
  id: string;
  /** 主表示（業務名 / 作業種別名） */
  label: string;
  /** 単価（円）。undefined なら非表示 */
  price?: number;
  /** "/筆" など */
  priceSuffix?: string;
  /** 業種 or セクション名 */
  badge?: string;
  badgeColor?: string;
  /** "前回 7/3" "使用 12 回" 等の補助情報 */
  meta?: string;
  /** 束のとき行数 */
  bundleSize?: number;
  /** 束の内訳（選択中にプレビュー表示する） */
  bundleItems?: Array<{ label: string; price?: number }>;
  /** label 以外にも検索対象にしたい文字列（説明・別名など） */
  keywords?: string;
};

type Props = {
  options: ComboboxOption[];
  onSelect: (opt: ComboboxOption) => void;
  /** 候補に無い文字列の確定を許すか。請求書=true / 計算書=false */
  allowFreeText?: boolean;
  onFreeTextCommit?: (text: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** 確定後に入力をクリアして次の入力に備えるか（既定 true） */
  clearOnSelect?: boolean;
  emptyMessage?: string;
  /** 一度に描画する候補の上限。超過分は「他 N 件」と明示する（既定 40） */
  maxVisible?: number;
  disabled?: boolean;
  /** 候補リストの下端に常設する要素（管理画面への導線など） */
  footer?: ReactNode;
  /** Esc（リストが閉じている状態）で呼ばれる */
  onEscape?: () => void;
  style?: CSSProperties;
  inputStyle?: CSSProperties;
  "aria-label"?: string;
};

export type WorkItemComboboxHandle = {
  focus: () => void;
  clear: () => void;
  getQuery: () => string;
};

/** 検索用の正規化: 全角→半角・大文字→小文字・カタカナ→ひらがな・空白除去 */
export function normalizeSearchText(value: string): string {
  const nfkc = value.normalize("NFKC").toLowerCase();
  const kana = nfkc.replace(/[ァ-ヶ]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0x60),
  );
  return kana.replace(/[\s　]/g, "");
}

/** 前方一致 → 部分一致（label）→ 部分一致（keywords/badge）の順に並べる */
export function filterComboboxOptions(
  options: ComboboxOption[],
  query: string,
): ComboboxOption[] {
  const q = normalizeSearchText(query);
  if (!q) return options;

  const prefix:    ComboboxOption[] = [];
  const partial:   ComboboxOption[] = [];
  const secondary: ComboboxOption[] = [];
  for (const opt of options) {
    const label = normalizeSearchText(opt.label);
    if (label.startsWith(q)) { prefix.push(opt); continue; }
    if (label.includes(q))   { partial.push(opt); continue; }
    const extra = normalizeSearchText(`${opt.keywords ?? ""} ${opt.badge ?? ""}`);
    if (extra && extra.includes(q)) secondary.push(opt);
  }
  return [...prefix, ...partial, ...secondary];
}

export const WorkItemCombobox = forwardRef<WorkItemComboboxHandle, Props>(
  function WorkItemCombobox(
    {
      options,
      onSelect,
      allowFreeText = false,
      onFreeTextCommit,
      placeholder = "業務名を入力して検索",
      autoFocus = false,
      clearOnSelect = true,
      emptyMessage = "候補がありません",
      maxVisible = 40,
      disabled = false,
      footer,
      onEscape,
      style,
      inputStyle,
      "aria-label": ariaLabel,
    },
    ref,
  ) {
    const [query, setQuery]       = useState("");
    const [open, setOpen]         = useState(false);
    const [rawActiveIdx, setActive] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef  = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
    const baseId = useId();
    const listId = `${baseId}-listbox`;

    useImperativeHandle(ref, () => ({
      focus: () => { inputRef.current?.focus(); inputRef.current?.select(); },
      clear: () => { setQuery(""); setActive(0); },
      getQuery: () => query,
    }), [query]);

    const filtered = useMemo(
      () => filterComboboxOptions(options, query),
      [options, query],
    );
    const visible   = filtered.slice(0, maxVisible);
    const overflow  = filtered.length - visible.length;

    // 自由記述の確定行を候補の末尾に 1 つ足す（Excel と同じく辞書に無い業務でも止まらない）
    const freeTextIdx =
      allowFreeText && query.trim().length > 0 ? visible.length : -1;
    const rowCount = visible.length + (freeTextIdx >= 0 ? 1 : 0);
    // 候補数が減ったときに範囲外を指さないようクランプする
    const activeIdx = rowCount === 0 ? 0 : Math.min(rawActiveIdx, rowCount - 1);

    // 入力が変わったら選択位置を先頭へ
    useEffect(() => { setActive(0); }, [query]);

    // 選択中候補が見えるようスクロール追従
    useEffect(() => {
      if (!open) return;
      optionRefs.current[activeIdx]?.scrollIntoView({ block: "nearest" });
    }, [activeIdx, open]);

    function commitOption(opt: ComboboxOption) {
      onSelect(opt);
      if (clearOnSelect) setQuery("");
      setActive(0);
      setOpen(false);
    }

    function commitFreeText() {
      const text = query.trim();
      if (!text || !allowFreeText) return;
      onFreeTextCommit?.(text);
      if (clearOnSelect) setQuery("");
      setActive(0);
      setOpen(false);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (!open) { setOpen(true); return; }
        if (rowCount === 0) return;
        const delta = e.key === "ArrowDown" ? 1 : -1;
        setActive((activeIdx + delta + rowCount) % rowCount);
        return;
      }
      if (e.key === "Enter") {
        // IME 変換確定の Enter は拾わない
        if (e.nativeEvent.isComposing) return;
        e.preventDefault();
        const picked = open ? visible[activeIdx] : undefined;
        if (picked) {
          commitOption(picked);
          return;
        }
        // 候補ヒット無し / 自由記述行を選択中
        commitFreeText();
        return;
      }
      if (e.key === "Escape") {
        if (open) { e.preventDefault(); setOpen(false); return; }
        onEscape?.();
        return;
      }
      if (e.key === "Tab") {
        setOpen(false);
      }
    }

    const wrapStyle: CSSProperties = { position: "relative", width: "100%", ...style };

    const fieldStyle: CSSProperties = {
      padding: "4px 8px",
      border: "1px solid #cbd5e1",
      borderRadius: 6,
      fontSize: 13,
      width: "100%",
      background: disabled ? "#f8fafc" : "#fff",
      fontFamily: "inherit",
      boxSizing: "border-box",
      ...inputStyle,
    };

    return (
      <div style={wrapStyle}>
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-activedescendant={
            open && rowCount > 0 ? `${baseId}-opt-${activeIdx}` : undefined
          }
          aria-label={ariaLabel}
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          style={fieldStyle}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
        />

        {open && (
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 3px)",
              left: 0,
              minWidth: "100%",
              maxWidth: 520,
              zIndex: 60,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
              maxHeight: 288,
              overflowY: "auto",
              padding: 4,
            }}
            // 入力の blur より先に mousedown が走るので既定動作を止める
            onMouseDown={(e) => e.preventDefault()}
          >
            {visible.map((opt, idx) => {
              const active = idx === activeIdx;
              return (
                <div
                  key={opt.id}
                  id={`${baseId}-opt-${idx}`}
                  ref={(el) => { optionRefs.current[idx] = el; }}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => commitOption(opt)}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 6,
                    cursor: "pointer",
                    background: active ? "#f0fdfa" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: "#0f172a",
                      flex: 1, minWidth: 0, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {opt.label}
                    </span>

                    {opt.bundleSize && opt.bundleSize > 1 ? (
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: "#c2410c",
                        background: "#fff7ed", border: "1px solid #fed7aa",
                        borderRadius: 99, padding: "1px 7px", whiteSpace: "nowrap",
                      }}>
                        {opt.bundleSize}行
                      </span>
                    ) : opt.price !== undefined ? (
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: "#0f766e", whiteSpace: "nowrap",
                      }}>
                        {formatJPY(opt.price)}{opt.priceSuffix ?? ""}
                      </span>
                    ) : null}

                    {opt.badge && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: opt.badgeColor ?? "#475569",
                        background: `${opt.badgeColor ?? "#475569"}18`,
                        borderRadius: 99, padding: "1px 7px", whiteSpace: "nowrap",
                      }}>
                        {opt.badge}
                      </span>
                    )}
                  </div>

                  {opt.meta && (
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{opt.meta}</div>
                  )}

                  {/* 束は選択中に内訳をプレビュー（押すまで中身が分からない問題の是正） */}
                  {active && opt.bundleItems && opt.bundleItems.length > 0 && (
                    <div style={{
                      marginTop: 4, paddingTop: 4, borderTop: "1px dashed #e2e8f0",
                      display: "grid", gap: 2,
                    }}>
                      {opt.bundleItems.map((bi, i) => (
                        <div key={i} style={{
                          display: "flex", justifyContent: "space-between", gap: 8,
                          fontSize: 11, color: "#64748b",
                        }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            ・{bi.label}
                          </span>
                          {bi.price !== undefined && <span>{formatJPY(bi.price)}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {freeTextIdx >= 0 && (
              <div
                id={`${baseId}-opt-${freeTextIdx}`}
                ref={(el) => { optionRefs.current[freeTextIdx] = el; }}
                role="option"
                aria-selected={activeIdx === freeTextIdx}
                onMouseEnter={() => setActive(freeTextIdx)}
                onClick={commitFreeText}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 12,
                  color: "#0f766e",
                  fontWeight: 700,
                  borderTop: visible.length > 0 ? "1px solid #f1f5f9" : undefined,
                  marginTop: visible.length > 0 ? 2 : 0,
                  background: activeIdx === freeTextIdx ? "#f0fdfa" : "transparent",
                }}
              >
                ＋「{query.trim()}」をこのまま追加
              </div>
            )}

            {rowCount === 0 && (
              <div style={{ padding: "8px 10px", fontSize: 12, color: "#94a3b8" }}>
                {emptyMessage}
              </div>
            )}

            {overflow > 0 && (
              <div style={{ padding: "5px 8px", fontSize: 11, color: "#94a3b8" }}>
                他 {overflow} 件（絞り込んでください）
              </div>
            )}

            {footer && (
              <div style={{
                borderTop: "1px solid #f1f5f9", marginTop: 2, padding: "5px 8px", fontSize: 11,
              }}>
                {footer}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
